/**
 * Portal Login Page - World Class Minimalist Design
 * Apple meets ChatGPT meets Lexy
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, ArrowRight, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PortalLogin: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [linkSent, setLinkSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  useEffect(() => {
    if (token) {
      loginWithToken(token);
    }
  }, [token]);
  
  const loginWithToken = async (magicToken: string) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/portal/auth/magic-link`, {
        token: magicToken
      });
      
      if (response.data.success) {
        localStorage.setItem('portal_token', response.data.token);
        localStorage.setItem('portal_client', JSON.stringify(response.data.client));
        localStorage.setItem('portal_branding', JSON.stringify(response.data.branding));
        toast.success('Welcome!');
        navigate('/portal/dashboard');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Invalid or expired link');
    } finally {
      setLoading(false);
    }
  };
  
  const requestNewLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    setErrorMessage('');
    try {
      await axios.post(`${API}/api/portal/auth/request-new-link?email=${encodeURIComponent(email)}`);
      setLinkSent(true);
    } catch (error: any) {
      const detail = error.response?.data?.detail || 'Could not send link';
      setErrorMessage(detail);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDemoLogin = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/portal/auth/demo-login`);
      if (response.data.success) {
        localStorage.setItem('portal_token', response.data.token);
        localStorage.setItem('portal_client', JSON.stringify(response.data.client));
        localStorage.setItem('portal_branding', JSON.stringify(response.data.branding));
        toast.success('Welcome!');
        navigate('/portal/dashboard');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };
  
  // Loading state when using magic link
  if (token && loading) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="relative w-16 h-16 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-violet-500 to-indigo-500 animate-spin" style={{ animationDuration: '2s' }} />
            <div className="absolute inset-1 rounded-full bg-[#fafafa]" />
            <div className="absolute inset-3 rounded-full bg-gradient-to-r from-violet-500 to-indigo-500" />
          </div>
          <p className="text-gray-500 font-light">Signing you in...</p>
        </motion.div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#fafafa] flex flex-col">
      {/* Subtle gradient background */}
      <div className="fixed inset-0 bg-gradient-to-br from-violet-50/50 via-transparent to-indigo-50/50 pointer-events-none" />
      
      {/* Main content */}
      <div className="flex-1 flex items-center justify-center p-6 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-sm"
        >
          {/* Logo */}
          <div className="text-center mb-12">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center shadow-2xl shadow-gray-900/20"
            >
              <Sparkles className="w-8 h-8 text-white" />
            </motion.div>
            <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
              Client Portal
            </h1>
            <p className="text-gray-500 mt-2 text-sm font-light">
              Secure access to your legal matters
            </p>
          </div>
          
          {/* Card */}
          <AnimatePresence mode="wait">
            {linkSent ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-3xl p-8 shadow-xl shadow-gray-200/50"
              >
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-500 flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">Check your inbox</h2>
                  <p className="text-gray-500 text-sm mb-6">
                    We've sent a login link to<br/>
                    <span className="font-medium text-gray-900">{email}</span>
                  </p>
                  <button
                    onClick={() => setLinkSent(false)}
                    className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    Use a different email
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-3xl p-8 shadow-xl shadow-gray-200/50"
              >
                <form onSubmit={requestNewLink} className="space-y-4">
                  <div>
                    <input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setErrorMessage(''); }}
                      className="w-full h-14 px-5 bg-gray-50 rounded-2xl border-0 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 transition-all text-center"
                      required
                    />
                  </div>
                  
                  {errorMessage && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-sm text-amber-600 text-center bg-amber-50 rounded-xl py-3 px-4"
                    >
                      {errorMessage}
                    </motion.p>
                  )}
                  
                  <button
                    type="submit"
                    disabled={loading || !email}
                    className="w-full h-14 bg-gray-900 hover:bg-gray-800 disabled:bg-gray-200 text-white disabled:text-gray-400 rounded-2xl font-medium transition-all flex items-center justify-center gap-2 group"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </button>
                </form>
                
                {/* Demo login */}
                <div className="mt-8 pt-6 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={handleDemoLogin}
                    disabled={loading}
                    className="w-full h-12 bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-600 hover:to-indigo-600 text-white rounded-2xl font-medium transition-all text-sm flex items-center justify-center gap-2"
                  >
                    <Sparkles className="w-4 h-4" />
                    Try Demo Portal
                  </button>
                  <p className="text-xs text-gray-400 text-center mt-3">Experience the portal as a demo client</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
      
      {/* Footer */}
      <div className="p-6 text-center">
        <p className="text-xs text-gray-400">
          Secured with end-to-end encryption
        </p>
      </div>
    </div>
  );
};

export default PortalLogin;
